import { z } from 'zod';

/**
 * Talen waarin *inhoud* gelezen kan worden.
 *
 * Dit is met opzet een ander begrip dan `Language` uit `auth`. Daar staan de drie talen waarin
 * de **interface** bestaat (nl, fr, en) — knoppen, foutmeldingen, brieven. Hier staat de taal
 * waarin een medewerker **berichten** leest, en dat is alles wat de vertaaldienst aankan.
 *
 * Op een productievloer werken vaak zes tot tien nationaliteiten. Die mensen een interface in
 * hun eigen taal geven is een project; hun de veiligheidsinstructie in hun eigen taal laten
 * lezen is deze stap. Het tweede is het dringende.
 *
 * Vorm: een ISO 639-1-code, eventueel met een regio erachter (`pt-BR`, `zh-Hans`). De echte
 * lijst komt van de provider; deze regex houdt alleen onzin buiten de deur.
 */
export const contentLanguageSchema = z
  .string()
  .regex(/^[a-z]{2}(-[A-Za-z]{2,4})?$/, 'Geen geldige taalcode.');
export type ContentLanguage = z.infer<typeof contentLanguageSchema>;

/**
 * Eén taal in de keuzelijst.
 *
 * `name` staat in de taal zelf — "Română" en niet "Roemeens". Wie de lijst moet gebruiken,
 * herkent zijn eigen taal, en die persoon leest de interface per definitie niet vlot.
 */
export const translationLanguageSchema = z.object({
  code: contentLanguageSchema,
  /** De naam van de taal, in die taal. */
  name: z.string().min(1),
});
export type TranslationLanguage = z.infer<typeof translationLanguageSchema>;

export const translationLanguagesSchema = z.object({
  /** Staat vertalen aan voor dit bedrijf? Zo niet, dan is `languages` de bronlijst van de interface. */
  enabled: z.boolean(),
  languages: z.array(translationLanguageSchema),
});
export type TranslationLanguages = z.infer<typeof translationLanguagesSchema>;

// --- Verbruik ---------------------------------------------------------------------------

/**
 * Wat een bedrijf in een maand vertaald heeft.
 *
 * Tekens en niet woorden of berichten, want dat is waar een vertaaldienst op rekent. Het staat
 * op het scherm van de platformbeheerder omdat het geld kost en omdat een bedrijf dat plots
 * tien keer zo veel vertaalt, meestal iets anders aan de hand heeft.
 */
export const translationUsageMonthSchema = z.object({
  /** De maand als `YYYY-MM`. */
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  characters: z.number().int().min(0),
  requests: z.number().int().min(0),
});
export type TranslationUsageMonth = z.infer<typeof translationUsageMonthSchema>;

export const translationUsageSchema = z.object({
  months: z.array(translationUsageMonthSchema),
  /** De maandgrens in tekens; erboven krijgt de lezer het origineel. */
  limitCharacters: z.number().int().min(0),
  /** Tekens deze maand, apart omdat dat het getal is waar iemand naar kijkt. */
  currentMonthCharacters: z.number().int().min(0),
});
export type TranslationUsage = z.infer<typeof translationUsageSchema>;
